// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Scrollable message area (FR-8, FR-13, FR-16, FR-17). Renders bubbles from
// data, attaches the checklist card and follow-up chips to assistant turns,
// shows the typing indicator while pending, offers Retry on failure, and keeps
// the latest message anchored at the bottom.

import { useEffect, useRef } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';
import { EliaAvatar } from './EliaAvatar';
import { EliaMessageBubble } from './EliaMessageBubble';
import { EliaMessageCard } from './EliaMessageCard';
import { EliaSuggestionChips } from './EliaSuggestionChips';
import { EliaTypingIndicator } from './EliaTypingIndicator';
import type { EliaMessage } from './elia-chat-types';

// Assistant identity mascot sits beside the bubble; extras align beneath it.
const AVATAR_SIZE = 28;
const AVATAR_GAP = 8;

function AssistantRow({
  message,
  busy,
  onSelectSuggestion,
  onRetry,
}: {
  message: EliaMessage;
  busy: boolean;
  onSelectSuggestion: (text: string) => void;
  onRetry: () => void;
}) {
  const isFailure = message.status === 'failed';
  const hasCard = !!message.card;
  const hasSuggestions = !!(message.suggestions && message.suggestions.length > 0);
  const hasExtras = isFailure || hasCard || hasSuggestions;
  // Keep the card / chips / retry aligned under the bubble (avatar + gap) so the
  // mascot sits beside the message text only — never beside suggestion chips.
  const extrasIndent = AVATAR_SIZE + AVATAR_GAP;

  return (
    <div>
      <div style={{ display: 'flex', gap: AVATAR_GAP, alignItems: 'flex-end' }}>
        <EliaAvatar size={AVATAR_SIZE} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <EliaMessageBubble role="assistant" content={message.content} collapsible={!isFailure} />
        </div>
      </div>
      {hasExtras ? (
        <div style={{ marginLeft: extrasIndent }}>
          {isFailure ? (
            <button
              type="button"
              onClick={onRetry}
              disabled={busy}
              style={{
                marginTop: 6,
                minHeight: 24,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'transparent',
                border: `1px solid ${ELIA_COLORS.border}`,
                borderRadius: 9999,
                padding: '5px 12px',
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.5 : 1,
                color: ELIA_COLORS.pink,
                fontFamily: ELIA_FONT,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              Retry
            </button>
          ) : null}
          {hasCard ? <EliaMessageCard card={message.card!} /> : null}
          {hasSuggestions ? (
            <EliaSuggestionChips
              suggestions={message.suggestions!}
              onSelect={onSelectSuggestion}
              disabled={busy}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function UserRow({ message }: { message: EliaMessage }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ maxWidth: '80%' }}>
        <EliaMessageBubble role="user" content={message.content} />
      </div>
    </div>
  );
}

export function EliaConversation({
  messages,
  busy,
  onSelectSuggestion,
  onRetry,
}: {
  messages: EliaMessage[];
  busy: boolean;
  onSelectSuggestion: (text: string) => void;
  onRetry: () => void;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Stable bottom-anchor: scroll to latest after new messages or typing (FR-17).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, busy]);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        fontFamily: ELIA_FONT,
      }}
    >
      {messages.map((message) =>
        message.role === 'assistant' ? (
          <AssistantRow
            key={message.id}
            message={message}
            busy={busy}
            onSelectSuggestion={onSelectSuggestion}
            onRetry={onRetry}
          />
        ) : (
          <UserRow key={message.id} message={message} />
        ),
      )}
      {busy ? <EliaTypingIndicator /> : null}
      <div ref={bottomRef} />
    </div>
  );
}
